import React from 'react';
import Card from '../../../../shared/components/Card';
import usePageTranslation from '../../../../shared/hooks/usePageTranslation';

export interface AllianceListItem {
  id: string;
  name: string;
  slogan: string;
  memberCount: number;
  presentationText?: string;
  discordLink?: string;
}

interface AllianceListViewProps {
  alliances: AllianceListItem[];
  onJoin: (id: string) => void;
  onCreateClick: () => void;
  onViewPresentation?: (id: string) => void;
}

const AllianceListView: React.FC<AllianceListViewProps> = ({
  alliances,
  onJoin,
  onCreateClick,
  onViewPresentation,
}) => {
  const { t } = usePageTranslation('game', 'AlliancePage');

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h2 className="text-2xl font-bold text-white">{t('list.title')}</h2>
        <button
          type="button"
          onClick={onCreateClick}
          className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2 shrink-0"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          {t('list.createAlliance')}
        </button>
      </div>

      {alliances.length === 0 ? (
        <Card className="p-8 text-center">
          <p className="text-gray-400 mb-6">{t('list.empty')}</p>
          <button
            type="button"
            onClick={onCreateClick}
            className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium"
          >
            {t('list.createFirst')}
          </button>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {alliances.map((alliance) => (
            <Card key={alliance.id} className="p-6 flex flex-col">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-full bg-gray-700/80 border-2 border-indigo-500/50 flex items-center justify-center shrink-0">
                  <svg className="w-7 h-7 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-lg font-bold text-white truncate">{alliance.name}</h3>
                  <p className="text-sm text-gray-400 line-clamp-2 mt-1">{alliance.slogan}</p>
                  <p className="text-xs text-gray-500 mt-2">
                    {alliance.memberCount} {t('list.members')}
                  </p>
                </div>
              </div>
              <div className="mt-4 flex gap-2">
                {onViewPresentation && (
                  <button
                    type="button"
                    onClick={() => onViewPresentation(alliance.id)}
                    className="flex-1 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    {t('list.viewPresentation')}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => onJoin(alliance.id)}
                  className="flex-1 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium transition-colors"
                >
                  {t('list.join')}
                </button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AllianceListView;
